import { useEffect, useMemo, useRef, useState } from "react";
import { Play, Loader2, GraduationCap, Volume2, BookOpen, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const Academy = () => {
  const [data, setData] = useState(null);
  const [modules, setModules] = useState([]);
  const [manualFilter, setManualFilter] = useState("");
  const [active, setActive] = useState(null);
  const [audioBusy, setAudioBusy] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    api.academy().then((d) => { setData(d); setActive((d.lessons || [])[0] || null); }).catch(() => {});
    api.modules().then(setModules).catch(() => {});
  }, []);

  const modBySlug = useMemo(() => Object.fromEntries(modules.map((m) => [m.slug, m])), [modules]);
  const lessons = data?.lessons || [];
  const manuals = data?.manuals || {};
  const filtered = manualFilter ? lessons.filter((l) => l.manual === manualFilter) : lessons;

  const selectLesson = (l) => { setActive(l); setAudioUrl(null); };

  const listen = async () => {
    if (!active) return;
    setAudioBusy(true);
    try {
      const url = await api.academyAudio(active.id);
      setAudioUrl(url);
      setTimeout(() => audioRef.current?.play().catch(() => {}), 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audio indisponible.");
    } finally {
      setAudioBusy(false);
    }
  };

  if (!data) return <div className="text-slate-400 text-sm py-10 text-center">Chargement de l'académie…</div>;

  const manualBadge = (id) => (
    <span data-testid={`academy-manual-${id}`} className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: manuals[id]?.color || "#012141" }}>
      {id}
    </span>
  );

  return (
    <div data-testid="academy">
      <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
        <GraduationCap size={16} className="text-[#C89A3E]" />
        <span>Leçons de formation tirées des manuels AMF — lisez ou écoutez la version audio.</span>
      </div>
      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        <button data-testid="academy-filter-all" onClick={() => setManualFilter("")}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${!manualFilter ? "bg-[#012141] text-white border-[#012141]" : "bg-white text-[#012141] border-[#012141]/15 hover:border-[#C89A3E]"}`}>Tous</button>
        {Object.entries(manuals).map(([id, m]) => (
          <button key={id} data-testid={`academy-filter-${id}`} onClick={() => setManualFilter(id)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${manualFilter === id ? "text-white border-transparent" : "bg-white text-[#012141] border-[#012141]/15 hover:border-[#C89A3E]"}`}
            style={manualFilter === id ? { backgroundColor: m.color } : {}}>{m.label}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-2 max-h-[55vh] overflow-y-auto pr-1">
          {filtered.map((l) => (
            <button key={l.id} data-testid={`academy-lesson-${l.id}`} onClick={() => selectLesson(l)}
              className={`w-full text-left rounded-2xl border p-3 transition-all ${active?.id === l.id ? "border-[#C89A3E] bg-[#FDFDFD] shadow-sm" : "border-[#012141]/10 bg-white hover:border-[#C89A3E]"}`}>
              <div className="flex items-center gap-2 mb-1">{manualBadge(l.manual)}<span className="text-[10px] text-slate-400">{l.chapter}</span></div>
              <div className="font-heading font-bold text-[#012141] text-sm leading-snug">{l.title}</div>
            </button>
          ))}
        </div>

        {active && (
          <div data-testid="academy-reader" className="lg:col-span-2 rounded-2xl border border-[#012141]/10 bg-white p-5">
            <div className="flex items-center gap-2 mb-1">{manualBadge(active.manual)}<span className="text-xs text-slate-400">{manuals[active.manual]?.label} · {active.chapter}</span></div>
            <h3 className="font-heading text-2xl font-bold text-[#012141] leading-snug">{active.title}</h3>
            <p className="read-prose mt-2">{active.intro}</p>

            <div className="mt-3">
              <button data-testid="academy-listen-btn" onClick={listen} disabled={audioBusy}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#012141] text-white font-semibold text-sm hover:bg-[#00172E] disabled:opacity-50">
                {audioBusy ? <Loader2 size={16} className="animate-spin" /> : <Volume2 size={16} className="text-[#C89A3E]" />} Écouter la leçon
              </button>
              {audioUrl && <audio ref={audioRef} data-testid="academy-audio" src={audioUrl} controls className="mt-3 w-full" />}
            </div>

            {active.sections?.map((s, i) => (
              <div key={i} className="mt-4">
                <div className="font-bold text-[#012141] text-sm">{s.heading}</div>
                <ul className="mt-1 space-y-1">
                  {s.points.map((p, j) => <li key={j} className="text-sm text-slate-600 flex gap-2"><span className="text-[#C89A3E]">◆</span>{p}</li>)}
                </ul>
              </div>
            ))}

            {active.takeaways?.length > 0 && (
              <div className="mt-4 rounded-xl bg-[#012141]/5 p-3">
                <div className="text-xs font-bold uppercase tracking-wide text-[#C89A3E] mb-1">À retenir</div>
                <ul className="space-y-1">{active.takeaways.map((t, i) => <li key={i} className="text-sm text-[#012141] flex gap-2"><span>✓</span>{t}</li>)}</ul>
              </div>
            )}

            {active.module_slugs?.length > 0 && (
              <div className="mt-4 pt-3 border-t border-[#012141]/8 flex flex-wrap gap-1.5">
                {active.module_slugs.map((sl) => modBySlug[sl] && (
                  <button key={sl} onClick={() => window.open(`/formations/${sl}`, "_blank", "noopener")}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#012141] bg-[#012141]/6 hover:bg-[#C89A3E]/20 px-2 py-1 rounded-full">
                    <BookOpen size={11} /> {modBySlug[sl].title} <ExternalLink size={10} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
